import React, { useEffect, useMemo } from 'react';
import { Modal, Button } from '../../../_custom';
import styles from '../../ManageDealDrawer.module.scss';
import { evaluateFormula } from '../../../../utils/utils';
import { InputRenderer } from './InputRenderer';

type CtaButton = {
    text: string;
    variant: 'primary' | 'secondary';
};

type InputObj = {
    label: string;
    subLabel?: string;
    type: string;
    isRequired?: boolean;
    placeholder: string;
    unit?: string;
    allowDecimal?: boolean;
    allowNegative?: boolean;
};

type ModalDetails = {
    heading: string;
    subHeading: string;
    formula?: {
        fallback: number;
        condition: string;
        expression: string;
    };
    inputObj: Record<string, InputObj>;
    ctaButton: Record<string, CtaButton>;
};

type RenderModalProps = {
    show: boolean;
    onClose: () => void;
    modalDetails: ModalDetails;
    formData: Record<string, any>;
    sectionKey: string;
    formKey: string;
    handleChange: (key: string, value: any) => void;
    auditedfinancialsMapping: Record<string, { year?: number; value: string }>;
    dealDrawerState?: any;
    activeFinancialsTab?: string;
    sectionDualDataStorage?: boolean;
};

export const RenderModal: React.FC<RenderModalProps> = ({
    show,
    onClose,
    modalDetails,
    formData,
    sectionKey,
    formKey,
    handleChange,
    auditedfinancialsMapping,
    dealDrawerState,
    activeFinancialsTab,
    sectionDualDataStorage,
}) => {
    // Shallow copy of formData to avoid direct mutation
    const [localFormData, setLocalFormData] = React.useState<Record<string, any>>(() => ({ ...formData }));

    useEffect(() => {
        setLocalFormData({ ...formData });
    }, [formData]);

    const handleLocalChange = (key: string, value: any) => {
        setLocalFormData((prev) => ({
            ...prev,
            [key]: value,
        }));
    };

    const buildFormKey = (fieldKey: string) => {
        if (!sectionKey) return fieldKey;
        if (!sectionDualDataStorage) return `${sectionKey}.${fieldKey}`;
        if (activeFinancialsTab) return `${sectionKey}.${activeFinancialsTab}.${fieldKey}`;
        return `${sectionKey}.${fieldKey}`;
    };

    // Not disabling primary button leaving this in case we need it in future
    const isAllDone = useMemo(() => {
        return Object.entries(modalDetails?.inputObj).every(([key, input]) => {
            if (input.isRequired) {
                const fullKey = buildFormKey(key);
                return !!localFormData[fullKey];
            }
            return true;
        });
    }, [modalDetails, localFormData, sectionKey, activeFinancialsTab, sectionDualDataStorage]);

    /*
      @TODO will replace this with switch case when we having more than different types
      of modals in Debt Products/Manage Deal Drawer. Type of modal will be decided
      based on config & requirments, still there is only one types of modals (Formula Modal).
    */
    const handlePrimaryAction = (e: React.MouseEvent) => {
        const isFormulaModal = modalDetails?.formula;
        if (!isFormulaModal || !localFormData) return;

        // Build the correct prefix based on dual data storage
        const keyPrefix = sectionDualDataStorage && activeFinancialsTab ? `${sectionKey}.${activeFinancialsTab}.` : `${sectionKey}.`;

        const variables = Object.entries(localFormData)
            .filter(([key]) => key.startsWith(keyPrefix))
            .reduce(
                (acc, [key, value]) => {
                    const variableName = key.replace(keyPrefix, '');
                    acc[variableName] = parseFloat(value) || 0;
                    return acc;
                },
                {} as Record<string, number>,
            );

        const result = evaluateFormula(isFormulaModal, variables);
        const finalResult = Number.isFinite(result)
            ? Number.isInteger(result)
                ? result.toString()
                : parseFloat(result.toFixed(4)).toString()
            : isFormulaModal.fallback?.toString() ?? '0';

        handleChange(formKey, finalResult);
    };

    const renderWithInputRenderer = () => {
        if (!modalDetails.inputObj) return null;
        return Object.entries(modalDetails.inputObj).map(([key, config]: [string, InputObj], index: number) => (
            <div key={index} className={styles.InputItem}>
                <InputRenderer
                    sectionKey={sectionKey}
                    fieldKey={key}
                    config={config}
                    formData={localFormData}
                    dealDrawerState={dealDrawerState}
                    handleChange={handleLocalChange}
                    auditedfinancialsMapping={auditedfinancialsMapping}
                    showFloatingLabel={true}
                    activeFinancialsTab={activeFinancialsTab}
                    sectionDualDataStorage={sectionDualDataStorage}
                />
            </div>
        ));
    };

    return (
        <Modal
            open={show}
            onClose={onClose}
            style={{
                height: 'auto',
                maxHeight: '90vh',
                padding: 0,
            }}
            closeIconStyle={{
                marginTop: '1rem',
            }}
        >
            <div className={styles.ModalContainer}>
                <div className={styles.ModalHeader}>{modalDetails?.heading}</div>
                <div className={styles.SubHeading}>{modalDetails?.subHeading}</div>

                <div className={styles.InputList}>{renderWithInputRenderer()}</div>

                <div className={styles.CtaButtons}>
                    {modalDetails?.ctaButton &&
                        Object.entries(modalDetails.ctaButton).map(([key, button]: [string, CtaButton], index: number) => (
                            <Button
                                key={index}
                                text={button.text}
                                variant={button.variant}
                                onClick={(e) => {
                                    e.preventDefault();
                                    if (button.variant === 'primary') {
                                        handlePrimaryAction(e);
                                        Object.entries(localFormData).forEach(([key, value]) => {
                                            if (value !== formData[key]) {
                                                handleChange(key, value);
                                            }
                                        });
                                    }
                                    onClose();
                                }}
                                style={{
                                    minWidth: '48%',
                                    marginTop: 0,
                                    ...(button.variant === 'primary' ? { color: '#FFFFFF' } : {}),
                                }}
                            />
                        ))}
                </div>
            </div>
        </Modal>
    );
};
